(ns ycweb.app.admin.stores.overview
  (:require
   [rum.core :as rum]
   [util.transform :as trf]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datepicker :as datepicker]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.editor.base :as editor]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.app.website.myaccount.common :as awm.common]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.date :as ud]
   [ycweb.util.urlstate :as urlstate]))

(def trading-days ["Monday" "Tuesday" "Wednesday" "Thursday" "Friday" "Saturday" "Sunday"])

(def edit-permissions
  ["SiteAdmin:Stores Edit"
   "SiteAdmin:Store:Regional Manager"
   "SiteAdmin:Store:Manager"])

(defn page-header [app-atom {:keys [title button-text url-action url-params]}]
  [:div.u-margin-bottom-40
   {:class
    (u/bem :flex-grid-group [:valign-middle :align-left :flush-force])}
   [:div
    {:class (u/bem :flex-grid-block [:grow-1 :fluid])}
    [:div.flex-grid-content
     (headings/heading title {:type :h1 :size :large :flush true})]]
   [:div
    {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
    [:div.flex-grid-content
     (buttons/button-web
       (or button-text "Back")
       (merge
         (urlstate/href-props app-atom "stores" url-action (or url-params {}))
         {:style :secondary
          :size :default
          :icon :arrow-left}))]]])

(defn stats-card [app-atom {:keys [title stats-content custom-content url-action url-params edit-permission?]}]
  [:article.stats-table-card.flex-grid-content
   [:header.stats-table-card__header
    {:class (u/bem :flex-grid-group [:valign-middle :align-left])}
    [:div
     {:class (u/bem :flex-grid-block [:grow-1 :fluid :flush])}
     [:div.flex-grid-content
      [:h2.stats-table-card__title
       {:class (u/bem :yc-heading [:flush :small])}
       title]]]
    (when edit-permission?
      [:div
       {:class (u/bem :flex-grid-block [:shrink-0 :fluid :flush])}
       [:div.flex-grid-content
        [:a.icon-link
         (merge
           (urlstate/href-props app-atom "stores" url-action url-params)
           {:class (u/bem :icon-block-link [:size-default])})
         [:i {:class (u/bem :icon [:edit :is-dark :default :block])}]
         [:span.u-hidden-from-view "Edit"]]]])]
   [:div.stats-table__content
    (when stats-content
      (into [:table.stats-table [:tbody]]
        (map
          (fn [stat]
            [:tr.stats-table__row
             [:th.stats-table__th (:title stat)]
             [:td.stats-table__td.u-text-right (:stat stat)]])
          stats-content)))
    (when custom-content
      custom-content)]])

(defn person-linked-role [users title]
  {:title title
   :stat
   (->>
     users
     (filter
       (fn [user]
         (contains? (:roles user) title)))
     (map
       (fn [user]
         (str (:firstname user) " " (:lastname user))))
     (interpose ", "))})

(defmethod grid/action :admin.stores/toggle-active [app-atom {:keys [store-id active]} _]
  (grid/confirm-modal app-atom
    {:title (str (if active "Deactivate" "Activate") " store")
     :text (str "Really " (if active "deactivate" "activate") " this store?")
     :confirm-key :admin.stores/update-store-active
     :confirm-text (str "Yes, " (if active "deactivate" "activate"))
     :cancel-text "Cancel"
     :negative active
     :store-id store-id
     :active active}))

(defmethod grid/action :admin.stores/update-store-active [app-atom {:keys [store-id active]} _]
  (comms/send :admin.stores/update-store-active {:store-id store-id :active (not active)})
  (toaster/open-toast app-atom :processing "Saving" {}))

(defmethod comms/receive :admin.stores/update-store-active [app-atom {:keys [active]}]
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :store-info :active] active)
  (toaster/open-toast app-atom :success "Saved" {}))

(defmethod grid/action :admin.stores/toggle-collectionenabled [app-atom {:keys [store-id collectionenabled]} _]
  (grid/confirm-modal app-atom
    {:title (str (if collectionenabled "Disable" "Enable") " collections")
     :text (str "Really " (if collectionenabled "disable" "enable") " collection?")
     :confirm-key :admin.stores/update-store-collectionenabled
     :confirm-text (str "Yes, " (if collectionenabled "disable" "enable"))
     :cancel-text "Cancel"
     :negative collectionenabled
     :store-id store-id
     :collectionenabled collectionenabled}))

(defmethod grid/action :admin.stores/update-store-collectionenabled [app-atom {:keys [store-id collectionenabled]} _]
  (comms/send :admin.stores/update-store-collectionenabled {:store-id store-id :collectionenabled (not collectionenabled)})
  (toaster/open-toast app-atom :processing "Saving" {}))

(defmethod comms/receive :admin.stores/update-store-collectionenabled [app-atom {:keys [collectionenabled]}]
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :store-info :collectionenabled] collectionenabled)
  (toaster/open-toast app-atom :success "Saved" {}))

(defmethod grid/action :admin.stores/toggle-archive-store [app-atom {:keys [store-id archived] :as config} _]
  (grid/confirm-modal app-atom
    {:title (str (if archived "Unarchive" "Archive") " store")
     :text (str "Really " (if archived "archive" "unarchive") " store?")
     :confirm-key :admin.stores/update-store-archived
     :confirm-text (str "Yes, " (if archived "unarchive" "archive"))
     :cancel-text "Cancel"
     :negative (not archived)
     :store-id store-id
     :archived archived}))

(defmethod grid/action :admin.stores/update-store-archived [app-atom {:keys [store-id archived]} _]
  (comms/send :admin.stores/update-store-archived {:store-id store-id :archived (not archived)})
  (toaster/open-toast app-atom :processing "Saving" {}))

(defmethod comms/receive :admin.stores/update-store-archived [app-atom {:keys [archived msg]}]
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :store-info :archived] archived)
  (toaster/open-toast app-atom :success (str (if archived "Archived" "Unarchived") ". " msg) {:closetime 5000}))

;; Store Summary =======================================================================================================

(def ip-address-grid-columns
  [{:key :ipaddress/ip-address
    :title "IP Address"}
   {:key :ipaddress/subnet
    :title "Subnet"}
   {:key :ipaddress/dns
    :title "DNS"}])

(def ip-address-table-config
  {:table-name :admin.stores/ip-addresses-table
   :row-id :ipaddress/id
   :options
   {:order :asc
    :sticky-header? true
    :paging? true
    :pagination {:page 0 :page-size 15}}
   :data-source
   {:cursor [:admin.stores :store :ip-adddresses]}
   :buttons
   [{:text "Add"
     :editor :admin.stores/ip-addresses-editor
     :extends :create}]
   :column-defs ip-address-grid-columns})

(defn ip-address-editor-config [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])]
    {:editor-name :admin.stores/ip-addresses-editor
     :datatable :admin.stores/ip-addresses-table
     :mode :read
     :title "Add a new IP Address"
     :end-points {:create :admin.stores/add-ip-address}
     :fields
     [{:name :store/id
       :type :hidden
       :attr {:default (:id store-info)}}
      {:name :ipaddress/ip-address
       :type :text
       :label "IP Address:"
       :attr
       {:type :text
        :required true
        :placeholder "192.168.000.000"}}
      {:name :ipaddress/subnet
       :type :text
       :label "Subnet:"
       :attr {:type :text :default "32" :required true}}
      {:name :ipaddress/dns
       :type :text
       :label "DNS:"
       :attr {:type :text}}]}))

(rum/defc store-settings-overview < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-atom (rum/cursor process-atom :store)
        store-info (cmn/curact store-atom :store-info)
        users (cmn/curact store-atom :users)
        titles ["Regional Manager" "Manager" "Assistant Manager"]
        active? (trf/toboolean (:active store-info))
        archived? (trf/toboolean (:archived store-info))
        edit-permission? (cmn/has-any-permission? app-atom edit-permissions)

        trading-hours-idx
        (->>
          (cmn/curact store-atom :trading-hours)
          (u/index-by :weekday))

        special-hours (cmn/curact store-atom :special-hours)
        special-hours-idx
        (->>
          (cmn/curact store-atom :special-hours)
          (u/index-by :special_date))]

    [:div
     (page-header app-atom
       {:title (str (:name store-info) " store overview")
        :button-text "Back to all stores"})

     [:div
      {:class (u/bem :flex-grid-group [:flush-force :align-left])}
      [:div {:class (u/bem :flex-grid-block [:full])}
       (stats-card app-atom
         {:title "Address & contact details"
          :url-params {:id (:id store-info)}
          :url-action "address"
          :edit-permission? edit-permission?
          :custom-content
          [:div
           [:p
            {:class (u/bem :yc-paragraph [:small :flush])}
            (:address store-info)
            [:br]
            (:suburb store-info)
            [:br]
            (:city store-info)
            [:br]
            (:postalcode store-info)]
           [:ul.u-no-list-bullets
            {:class (u/bem :yc-list [:small :flush])}
            [:li
             [:i
              {:class
               (u/bem :icon [:phone :is-dark :small :in-copy-left :inline])}]
             [:span (:tel store-info)]]
            [:li
             [:i
              {:class
               (u/bem :icon [:email :is-dark :small :in-copy-left :inline])}]
             [:span
              [:a.text-link
               {:href (str "mailto:" (:email store-info))}
               (:email store-info)]]]]]})]

      [:div {:class (u/bem :flex-grid-block [:ml-1-2])}
       (stats-card app-atom
         {:title "Operating hours"
          :url-params {:id (:id store-info)}
          :url-action "trading-hours"
          :edit-permission? edit-permission?
          :stats-content
          (map
            (fn [day]
              {:title day
               :stat
               (cond
                 (= (get-in trading-hours-idx [day :open_time]) (get-in trading-hours-idx [day :close_time]))
                 "Closed"

                 (get-in trading-hours-idx [day :open_time])
                 (str (get-in trading-hours-idx [day :open_time])  " - "
                   (get-in trading-hours-idx [day :close_time]))

                 :else
                 "Closed")})
            trading-days)})]

      [:div {:class (u/bem :flex-grid-block [:ml-1-2])}
       (stats-card app-atom
         {:title "Special hours"
          :url-params {:id (:id store-info)}
          :url-action "special-hours"
          :edit-permission? edit-permission?
          :stats-content
          (concat
            (map
              (fn [day]
                (let [special-date (:special_date day)]
                  {:title (str special-date " (" (:description day) ")")
                   :stat
                   (cond
                     (= (get-in special-hours-idx [special-date :open_time]) (get-in special-hours-idx [special-date :close_time]))
                     "Closed"

                     (get-in special-hours-idx [special-date :open_time])
                     (str (get-in special-hours-idx [special-date :open_time])  " - "
                       (get-in special-hours-idx [special-date :close_time]))

                     :else
                     "Closed")}))
              special-hours))})]

      [:div {:class (u/bem :flex-grid-block [:ml-1-2])}
       (stats-card app-atom
         {:title "Store staff"
          :url-params {:id (:id store-info)}
          :url-action "storestaff"
          :edit-permission? edit-permission?
          :stats-content
          (vec
            (map
              (fn [title]
                (person-linked-role users title))
              titles))})]

      [:div {:class (u/bem :flex-grid-block [:ml-1-2])}
       (stats-card app-atom
         {:title "Store metadata"
          :url-params {:id (:id store-info)}
          :url-action "metadata"
          :edit-permission? edit-permission?
          :stats-content
          [{:title "Liquor license:" :stat (:liquorlicense store-info)}
           {:title "Meraki network ID:" :stat (:location_network_refid store-info)}
           {:title "Lockdown restriction level:" :stat (:essentialgood store-info)}
           {:title "Store collections enabled date:" :stat (ud/date-format (:date_collections_active store-info) "dd MMM YYYY")}
           {:title "Store opening date:" :stat (ud/date-format (:date_store_active store-info) "dd MMM YYYY")}]})]

      (when (cmn/has-any-permission? app-atom ["SiteAdmin:Teams:IT" "SiteAdmin:Teams:Development"])
        [:div {:class (u/bem :flex-grid-block [:ml-1-2])}
         [:article.stats-table-card.flex-grid-content
          [:header.stats-table-card__header
           {:class (u/bem :flex-grid-group [:valign-middle :align-left])}
           [:div
            {:class (u/bem :flex-grid-block [:grow-1 :fluid :flush])}
            [:div.flex-grid-content
             [:h2.stats-table-card__title
              {:class (u/bem :yc-heading [:flush :small])}
              "Store IP Addresses"]]]]
          (datatables/table app-atom :admin.stores/ip-addresses-table {})]])]

     (when edit-permission?
       [:div.u-margin-top-40
        {:class (u/bem :flex-grid-group [:align-left :flush-force])}
        [:div
         {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
         [:div.flex-grid-content
          (buttons/button-web
            (str (if active? "Deactivate" "Activate") " store")
            {:icon (if active? :close :check)
             :style (if active? :negative :primary)
             :on-click
             #(grid/dispatch-action app-atom :admin.stores/toggle-active
                {:store-id (:id store-info)
                 :active active?})})]]
        [:div
         {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
         [:div.flex-grid-content
          (buttons/button-web
            (str (if (:collectionenabled store-info) "Disable" "Enable") " collection")
            {:icon (if (:collectionenabled store-info) :close :check)
             :style (if (:collectionenabled store-info) :negative :primary)
             :on-click
             #(grid/dispatch-action app-atom :admin.stores/toggle-collectionenabled
                {:store-id (:id store-info)
                 :collectionenabled (:collectionenabled store-info)})})]]
        [:div
         {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
         [:div.flex-grid-content
          (buttons/button-web
            (str (if archived? "Unarchive" "Archive") " store")
            {:icon (if archived? :check :close)
             :style (if archived? :primary :negative)
             :on-click
             #(grid/dispatch-action app-atom :admin.stores/toggle-archive-store
                {:store-id (:id store-info)
                 :archived archived?})})]]])]))

(defmethod flow/render-screen :admin.stores/overview [app-atom _]
  (cmn/permission-lock app-atom "SiteAdmin"
    (store-settings-overview app-atom)))

(defmethod t/transition-open :admin.stores/overview [app-atom _ {:keys [id] :as params}]
  (let [process-atom (cmn/process-cursor app-atom)]
    (toaster/open-overlay app-atom :admin.stores/overview)
    (swap! process-atom assoc-in [:store :store-info] {:id id})
    (comms/send :admin.stores/details {:id id})))

(defmethod t/transition-after-open :admin.stores/overview [app-atom _ _]
  (datatables/initiate app-atom ip-address-table-config {})
  (editor/initiate app-atom (ip-address-editor-config app-atom)))

(defmethod comms/receive :admin.stores/details
  [app-atom {:keys [users trading-hours special-hours store-info provinces ip-adddresses]}]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom update :store assoc
      :store-info store-info
      :users (vec users)
      :trading-hours trading-hours
      :special-hours special-hours
      :provinces provinces
      :ip-adddresses ip-adddresses))
  (toaster/close-overlay app-atom :admin.stores/loading-overlay))

(defmethod comms/receive :admin.stores/add-ip-address [app-atom {:keys [row] :as e}]
  (let [process-atom (cmn/process-cursor app-atom)
        store-atom (rum/cursor process-atom :store)]
    (swap! store-atom update :ip-adddresses assoc (:ipaddress/id row) row)
    (editor/post-submit app-atom :admin.stores/ip-addresses-editor e)))

;; Store Address =======================================================================================================

(rum/defcs store-settings-address < rum/reactive cmn/cursors (cmn/local nil ::node-atom) [local-atom app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        node-atom (::node-atom local-atom)
        store-atom (rum/cursor-in process-atom [:store :store-info])
        store-info (rum/react store-atom)
        provinces (cmn/curact process-atom [:store :provinces])]

    [:div
     (page-header app-atom
       {:title (str (:name store-info) " address & contact details")
        :button-text "Back to store overview"
        :url-action "overview"
        :url-params {:id (:id store-info)}})

     [:form.form.u-max-width-480
      {:ref (fn [node] (reset! node-atom node))}

      (forms/input "Store name:" "" (rum/cursor store-atom :name)
        {:type :text
         :required? true})

      (forms/textarea "Address:" (rum/cursor store-atom :address)
        {:rows 5
         :cols 50
         :hint
         [:span.form-hint.js-form-hint
          [:b "Note:"]
          " Include the shop number, mall name and street address."
          [:br]
          [:b "Formatting:"]
          " Write as a single line with commas."]})

      (forms/input "Suburb:" "" (rum/cursor store-atom :suburb)
        {:type :text
         :required? true})

      (forms/input "City:" "" (rum/cursor store-atom :city)
        {:type :text
         :required? true})

      [:div.form-field
       (forms/select "Province:"
         (rum/cursor store-atom :province_id)
         (into
           [{:value "" :text ""}]
           (map
             (fn [province] {:text (:name province) :value (:id province)})
             provinces)))]

      (forms/input "Postal code:" "" (rum/cursor store-atom :postalcode)
        {:type :text})

      (forms/input "Latitude:" "" (rum/cursor store-atom :latitude)
        {:type :location
         :icon? true})

      (forms/input "Longitude:" "" (rum/cursor store-atom :longitude)
        {:type :location
         :icon? true})

      (forms/input "Google Place ID:" "" (rum/cursor store-atom :googleplaceid)
        {:type :text
         :hint "Note: This will be filled in by the Marketing Team once we have a verified Google Places listing."})

      (forms/input "Maps link:" "" (rum/cursor store-atom :maps_link)
        {:type :url
         :required? true
         :icon? true
         :hint "Note: If you don't have the exact map link right now, link to the mall and be sure to update this later when we have a verified Google Places listing."})

      (forms/input "Telephone:" "" (rum/cursor store-atom :tel)
        {:type :tel
         :icon? true})

      (forms/input "Email:" "" (rum/cursor store-atom :email)
        {:type :email
         :icon? true
         :hide-email-helpers true})

      [:div.form-control
       (buttons/button-web "Save"
         {:style :primary
          :size :default
          :type :button
          :on-click
          (forms/form-validate node-atom
            #(grid/dispatch-action app-atom :admin.stores/update-store-details store-info))})]]
     (toaster/loading-overlay app-atom :admin.stores/loading-overlay)]))

(defmethod flow/render-screen :admin.stores/address [app-atom _]
  (cmn/multi-permission-lock app-atom edit-permissions
    (store-settings-address app-atom)))

(defmethod t/transition-open :admin.stores/address [app-atom _ {:keys [id]}]
  (toaster/open-overlay app-atom :admin.stores/loading-overlay)
  (comms/send :admin.stores/details {:id id}))

(defmethod grid/action :admin.stores/update-store-details [app-atom {:keys [id] :as store}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/update-store-details
    {:store-id id
     :store store}))

(defmethod comms/receive :admin.stores/update-store-details [app-atom {:keys [store]}]
  (toaster/open-toast app-atom :success "Saved" {})
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :store-info] store))

;; Store Hours =========================================================================================================

(def trading-hours-list
  [{:value "00:00" :text "- closed -"}
   {:value "06:00" :text "6:00"}
   {:value "06:30" :text "6:30"}
   {:value "07:00" :text "7:00"}
   {:value "07:30" :text "7:30"}
   {:value "08:00" :text "8:00"}
   {:value "08:30" :text "8:30"}
   {:value "09:00" :text "9:00"}
   {:value "09:30" :text "9:30"}
   {:value "10:00" :text "10:00"}
   {:value "10:30" :text "10:30"}
   {:value "11:00" :text "11:00"}
   {:value "11:30" :text "11:30"}
   {:value "12:00" :text "12:00"}
   {:value "12:30" :text "12:30"}
   {:value "13:00" :text "13:00"}
   {:value "13:30" :text "13:30"}
   {:value "14:00" :text "14:00"}
   {:value "14:30" :text "14:30"}
   {:value "15:00" :text "15:00"}
   {:value "15:30" :text "15:30"}
   {:value "16:00" :text "16:00"}
   {:value "16:30" :text "16:30"}
   {:value "17:00" :text "17:00"}
   {:value "17:30" :text "17:30"}
   {:value "18:00" :text "18:00"}
   {:value "18:30" :text "18:30"}
   {:value "19:00" :text "19:00"}
   {:value "19:30" :text "19:30"}
   {:value "20:00" :text "20:00"}
   {:value "20:30" :text "20:30"}
   {:value "21:00" :text "21:00"}
   {:value "21:30" :text "21:30"}
   {:value "22:00" :text "22:00"}
   {:value "22:30" :text "22:30"}
   {:value "23:00" :text "23:00"}
   {:value "23:30" :text "23:30"}])

(rum/defc trading-hours-day < rum/reactive [app-atom {:keys [day]}]
  (let [process-atom (cmn/process-cursor app-atom)
        time-atom (rum/cursor-in process-atom [:store :store-info :trading-hours])

        trading-hours-idx
        (->>
          (cmn/curact process-atom [:store :trading-hours])
          (u/index-by :weekday))]

    [:li.admin-times-list__item
     {:class
      (u/bem
        :flex-grid-group
        [:valign-middle :align-left :flush-force :no-wrap])}
     [:div.admin-times-list__day
      {:class (u/bem :flex-grid-block [:grow-1 :fluid])}
      [:div.flex-grid-content
       [:p
        {:class (u/bem :yc-paragraph [:flush])}
        [:b day]]]]
     [:div
      {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
      [:div.flex-grid-content "From"]]
     [:div
      {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
      [:div.flex-grid-content
       (forms/select-raw
         (rum/cursor-in time-atom [(keyword day) :open_time])
         trading-hours-list
         {:default (get-in trading-hours-idx [day :open_time])})]]
     [:div
      {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
      [:div.flex-grid-content "to"]]
     [:div
      {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
      [:div.flex-grid-content
       (forms/select-raw
         (rum/cursor-in time-atom [(keyword (str day)) :close_time])
         trading-hours-list
         {:default (get-in trading-hours-idx [day :close_time])})]]]))

(rum/defc store-settings-trading-hours  < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-atom (rum/cursor-in process-atom [:store :store-info])
        store-info (cmn/curact process-atom [:store :store-info])]

    [:div
     (page-header app-atom
       {:title (str (:name store-info) " operating hours")
        :button-text "Back to store overview"
        :url-action "overview"
        :url-params {:id (:id store-info)}})

     [:form.u-max-width-540
      [:ul.admin-times-list
       (map #(trading-hours-day app-atom {:day %}) trading-days)]
      [:div.form-control
       (buttons/button-web "Update operating hours"
         {:type :button
          :style :primary
          :size :default
          :on-click
          #(grid/dispatch-action app-atom :admin.stores/update-store-times
             {:store-id (:id store-info)
              :trading-hours (cmn/curact store-atom [:trading-hours])})})]]
     (toaster/loading-overlay app-atom :admin.stores/loading-overlay)]))

(defmethod flow/render-screen :admin.stores/trading-hours [app-atom _]
  (cmn/multi-permission-lock app-atom edit-permissions
    (store-settings-trading-hours app-atom)))

(defmethod t/transition-open :admin.stores/trading-hours [app-atom _ {:keys [id]}]
  (toaster/open-overlay app-atom :admin.stores/loading-overlay)
  (comms/send :admin.stores/details {:id id}))

(defmethod grid/action :admin.stores/update-store-times [app-atom {:keys [store-id trading-hours]}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/update-store-times
    {:store-id store-id
     :trading-hours trading-hours}))

(defmethod comms/receive :admin.stores/update-store-times [app-atom {:keys [store]}]
  (toaster/open-toast app-atom :success "Saved" {}))

;; Store Special Hours =================================================================================================

(rum/defc special-hours-day < rum/reactive cmn/cursors [app-atom [_ day]]

  [:tr.stats-table__row
   [:th.stats-table__th.u-valign-middle
    (str (:special_date day) " (" (:description day) ")" ":")]
   [:td.stats-table__td.u-text-right.u-valign-middle
    (cond
      (= (:open_time day) (:close_time day))
      "Closed"

      (:open_time day)
      (str (:open_time day)  " - " (:close_time day))

      :else
      "Closed")]
   [:td.stats-table__td.u-text-right.u-valign-middle
    (buttons/button-web ""
      {:style :secondary-noborder
       :size :tiny
       :type :button
       :class :circle
       :icon-only true
       :icon :delete
       :hidden-text "Delete this special date?"
       :on-click
       #(grid/dispatch-action app-atom :admin.stores/confirm-delete-store-special-time
          {:id (:id day)})})]])

(defmethod grid/action :admin.stores/delete-store-special-time [app-atom {:keys [id]}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/delete-store-special-time {:id id}))

(defmethod grid/action :admin.stores/confirm-delete-store-special-time [app-atom config _]
  (grid/confirm-modal app-atom
    {:title "Delete special time"
     :text "Are you sure you want to delete this?"
     :negative true
     :confirm-text "Yes, delete"
     :cancel-text "Cancel"
     :confirm-key :admin.stores/delete-store-special-time
     :id (:id config)}))

(defmethod comms/receive :admin.stores/delete-store-special-time [app-atom {:keys [special-hours]}]
  (toaster/open-toast app-atom :success "Saved" {})
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :special-hours] special-hours))

(rum/defc add-special-day < rum/reactive [app-atom]
  [:div.u-margin-bottom-24
   {:class (u/bem :flex-grid-group [:align-left :no-wrap :flush-force])}
   [:div
    {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
    [:div.flex-grid-content
     (datepicker/datepicker app-atom "Date:" "dd/mm/yy"
       [:store :add-date :special_date]
       {:header "Select date"
        :field-classes "flush"
        :datatype :date})]]
   [:div
    {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
    [:div.flex-grid-content
     (forms/input "Description:" "Christmas Day" (rum/cursor-in app-atom [:store :add-date :description])
       {:type :text
        :field-classes "flush"
        :required? true})]]

   [:div
    {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
    [:div.flex-grid-content
     [:div
      {:class (u/bem :form-field [:flush])}
      [:label.form-label "Open time:"]

      (forms/select-raw
        (rum/cursor-in app-atom [:store :add-date :open_time])
        trading-hours-list
        {:default "00:00"})]]]
   [:div
    {:class (u/bem :flex-grid-block [:shrink-0 :fluid])}
    [:div.flex-grid-content
     [:div
      {:class (u/bem :form-field [:flush])}
      [:label.form-label "Close time:"]
      (forms/select-raw
        (rum/cursor-in app-atom [:store :add-date :close_time])
        trading-hours-list
        {:default "00:00"})]]]])

(rum/defc store-settings-special-hours < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])

        special-hours-idx
        (->>
          (cmn/curact process-atom [:store :special-hours])
          (u/index-by :special_date))]
    [:div
     (page-header app-atom
       {:title (str (:name store-info) " special hours")
        :button-text "Back to store overview"
        :url-action "overview"
        :url-params {:id (:id store-info)}})

     (when (not-empty special-hours-idx)
       [:div.stats-table-card.u-max-width-480.u-margin-bottom-40
        [:h2 {:class (u/bem :yc-heading [:medium])} "Special operating hours"]
        [:table.stats-table
         [:colgroup [:col] [:col] [:col {:style {:width "64px"}}]]
         [:tbody
          (map #(special-hours-day app-atom %) special-hours-idx)]]])

     [:form.form
      (headings/heading "Add special operating hours" {:type :h2 :size :medium})
      [:p.yc-paragraph
       "Add special operating hours below by choosing a date, open time and close time — you'll need to add one for
        every day that differs from the default operating hours. If your store's operating hours are extended over a
        period, e.g. the festive season, set them there (you'll need to do it on the day it changes) and then use
        special hours for any day that differs from those."]

      (add-special-day app-atom)

      [:div.form-control
       (buttons/button-web "Save special date"
         {:style :primary
          :size :default
          :type :button
          :on-click
          #(grid/dispatch-action app-atom :admin.stores/update-store-special-times
             {:store-id (:id store-info)
              :special-trading-hours (cmn/curact app-atom [:store :add-date])})})]]
     (toaster/loading-overlay app-atom :admin.stores/loading-overlay)]))

(defmethod flow/render-screen :admin.stores/special-hours [app-atom _]
  (cmn/multi-permission-lock app-atom edit-permissions
    (store-settings-special-hours app-atom)))

(defmethod t/transition-open :admin.stores/special-hours [app-atom _ {:keys [id] :as params}]
  (toaster/open-overlay app-atom :admin.stores/loading-overlay)
  (comms/send :admin.stores/details {:id id}))

(defmethod grid/action :admin.stores/update-store-special-times [app-atom {:keys [store-id special-trading-hours]}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/update-store-special-times
    {:store-id store-id
     :special-trading-hours special-trading-hours}))

(defmethod comms/receive :admin.stores/update-store-special-times [app-atom {:keys [special-hours]}]
  (toaster/open-toast app-atom :success "Saved" {})
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :special-hours] special-hours))

;; Store Staff =========================================================================================================

(defn data-source-parameters [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])]
    {:store-id (:id store-info)}))

(defn remove-staff-link [app-atom row _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])]

    [:a
     {:on-click
      #(grid/dispatch-action app-atom :admin.stores/confirm-remove-store-staff
         {:store-id (:id store-info)
          :user-id (:userid row)})}
     "Remove from store"]))

(defn main-role [roles]
  (or
    (some #{"Regional Manager"} roles)
    (some #{"Manager"} roles)
    (some #{"Assistant Manager"} roles)
    "No Roles Assigned"))

(def store-staff-grid-fns
  {:data-source-fns
   {:store-fn (partial data-source-parameters)}
   :column-fns
   {:roles-fn (fn [_ row _ _] (main-role (:roles row)))
    :remove-fn remove-staff-link
    :permissions-fn
    (fn [_ row _ _]
      [:a
       {:href
        (str "/admin/users_edit.htm?overrideincludes=popup&sm[e1][id]=" (:userid row)),
        :target "_blank",
        :rel "nofollow"}
       "Change user permission"])}})

(def store-staff-grid-columns
  {:table-name :store-staff-table
   :row-id :id
   :data-source
   {:end-point :admin.stores/list-staff
    :parameter-fn :store-fn}
   :options
   {:order :desc
    :sort-title "Stores"}
   :buttons
   [{:text "Add staff"
     :editor :admin.stores/add-store-staff
     :extends :create}]
   :column-defs
   [{:key :email
     :title "Email"}
    {:key :roles
     :title "Roles"
     :render {:render-k :roles-fn}}
    {:key :id
     :title "Remove"
     :render {:render-k :remove-fn}}
    {:key :id
     :title "Permissions"
     :render {:render-k :permissions-fn}}]})

(defn staff-add-config [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])]
    {:editor-name :admin.stores/add-store-staff
     :datatable :store-staff-table
     :mode :read
     :title "Add a new staff member"
     :end-points
     {:create :admin.stores/add-store-staff}
     :fields
     [{:name :store-id
       :type :hidden
       :attr {:default (:id store-info)}}
      {:name :email
       :label "Staff member email address:"
       :attr
       {:type :text
        :placeholder "e.g. john.smith@domain.com"}}
      {:name :role
       :label "Select a store role:"
       :type :select
       :attr {:small? false}
       :options
       [{:value "" :text "- Select a store role -"}
        {:value "SiteAdmin:Store:Sales Assistant" :text "Sales Assistant"}
        {:value "SiteAdmin:Store:Assistant Manager" :text "Assistant Manager"}
        {:value "SiteAdmin:Store:Manager" :text "Manager"}
        {:value "SiteAdmin:Store:Regional Manager" :text "Regional Manager"}]}]}))

(rum/defc store-settings-storestaff < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-info (cmn/curact process-atom [:store :store-info])]

    [:div
     (page-header app-atom
       {:title (str (:name store-info) " staff")
        :button-text "Back to store overview"
        :url-action "overview"
        :url-params {:id (:id store-info)}})

     [:div.u-margin-top-40
      (headings/heading "" {:type :h3 :size :small})
      (datatables/table app-atom :store-staff-table store-staff-grid-fns)]

     (toaster/loading-overlay app-atom :admin.stores/loading-overlay)]))

(defmethod flow/render-screen :admin.stores/storestaff [app-atom _]
  (cmn/multi-permission-lock app-atom edit-permissions
    (store-settings-storestaff app-atom)))

(defmethod t/transition-open :admin.stores/storestaff [app-atom _ {:keys [id]}]
  (toaster/open-overlay app-atom :admin.stores/loading-overlay)
  (comms/send :admin.stores/details {:id id}))

(defmethod t/transition-after-open :admin.stores/storestaff [app-atom _ _]
  (if (nil? (get-in @app-atom [:datatables :editor :admin.stores/add-store-staff]))
    (datatables/initiate app-atom store-staff-grid-columns store-staff-grid-fns)
    (datatables/refresh app-atom :store-staff-table store-staff-grid-fns))
  (editor/initiate app-atom (staff-add-config app-atom)))

(defmethod comms/receive :admin.stores/list-staff [app-atom e]
  (datatables/post-comms app-atom :store-staff-table {:rows (:rows e)}))

(defmethod comms/receive :admin.stores/add-store-staff [app-atom e]
  (editor/post-submit app-atom :admin.stores/add-store-staff e)
  (datatables/add-row app-atom :store-staff-table {:row (:row e)}))

(defmethod grid/action :admin.stores/remove-store-staff [app-atom {:keys [store-id user-id] :as config}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/remove-store-staff {:store-id store-id :user-id user-id}))

(defmethod grid/action :admin.stores/confirm-remove-store-staff [app-atom config _]
  (grid/confirm-modal app-atom
    {:title "Remove staff link"
     :text "Are you sure you want to remove this user from the store staff?"
     :negative true
     :confirm-text "Yes, remove"
     :cancel-text "Cancel"
     :confirm-key :admin.stores/remove-store-staff
     :store-id (:store-id config)
     :user-id (:user-id config)}))

(defmethod comms/receive :admin.stores/remove-store-staff [app-atom {:keys [users]}]
  (let [process-atom (cmn/process-cursor app-atom)]
    (toaster/open-toast app-atom :success "Saved" {})
    (datatables/refresh app-atom :store-staff-table store-staff-grid-fns)
    (swap! process-atom update :store assoc :users (vec users))))

;; Store Metadata ======================================================================================================

(rum/defc store-settings-metadata < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        store-atom (rum/cursor-in process-atom [:store :store-info])
        store-info (rum/react store-atom)]

    [:div
     (page-header app-atom
       {:title (str (cmn/curact store-atom :name) " metadata")
        :button-text "Back to store overview"
        :url-action "overview"
        :url-params {:id (:id store-info)}})

     [:form.form.u-max-width-480
      (forms/input "Liquor license:" "" (rum/cursor store-atom :liquorlicense)
        {:type :text})

      (forms/input "Meraki Network ID:" "" (rum/cursor store-atom :location_network_refid)
        {:type :text})

      [:div.form-field.u-max-width-480
       (forms/select "Lockdown restriction level:"
         (rum/cursor-in store-atom [:essentialgood])
         [{:value "1" :text "1"}
          {:value "2" :text "2"}
          {:value "3" :text "3"}
          {:value "4" :text "4"}
          {:value "5" :text "5"}])]

      [:div.form-field.u-max-width-480
       (datepicker/datepicker app-atom "Store collections enabled date:" "dd/mm/yyyy"
         [:admin.stores :store :store-info :date_collections_active]
         {:header "Select date"
          :hint "Collections will automatically be enabled on this date"
          :datatype :datedmy
          :field-classes "flush"})]

      [:div.form-field.u-max-width-480
       (datepicker/datepicker app-atom "Store opening date:" "dd/mm/yyyy"
         [:admin.stores :store :store-info :date_store_active]
         {:header "Select date"
          :hint "The store will automatically be made active at 9pm on the day before this date"
          :datatype :datedmy
          :field-classes "flush"})]

      [:div.form-control
       (buttons/button-web "Save"
         {:style :primary
          :size :default
          :type :button
          :on-click
          #(grid/dispatch-action app-atom :admin.stores/update-store-metadata
             (cmn/curact process-atom [:store :store-info]))})]]
     (toaster/loading-overlay app-atom :admin.stores/loading-overlay)]))

(defmethod flow/render-screen :admin.stores/metadata [app-atom _]
  (cmn/multi-permission-lock app-atom edit-permissions
    (store-settings-metadata app-atom)))

(defmethod t/transition-open :admin.stores/metadata [app-atom _ {:keys [id]}]
  (toaster/open-overlay app-atom :admin.stores/loading-overlay)
  (comms/send :admin.stores/details {:id id}))

(defmethod grid/action :admin.stores/update-store-metadata [app-atom {:keys [id] :as store}]
  (toaster/open-toast app-atom :processing "Saving" {})
  (comms/send :admin.stores/update-store-metadata
    {:store-id id
     :store store}))

(defmethod comms/receive :admin.stores/update-store-metadata [app-atom {:keys [store]}]
  (toaster/open-toast app-atom :success "Saved" {})
  (swap! (cmn/process-cursor app-atom) assoc-in
    [:store :store-info] store))
